import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { MenuItem, SidebarSubmenuProps } from '../../../types/layout'
import { CUSTOM_ICONS } from '../../../config/menuConfig'

/**
 * Componente para el submenú desplegable del sidebar
 * Incluye animaciones suaves de slide-down y soporte de un nivel anidado
 */
const SidebarSubmenu: React.FC<SidebarSubmenuProps> = ({
  items,
  isCollapsed,
  isOpen,
  parentId: _parentId,
  hoveredItem: _hoveredItem,
  isMenuOpen,
  onToggle,
}) => {
  const location = useLocation()

  if (!isOpen) return null

  const isItemActive = (subItem: MenuItem): boolean => {
    const exactMatch = location.pathname === subItem.path
    if (exactMatch) return true

    const startsWithMatch = location.pathname.startsWith(subItem.path)
    if (!startsWithMatch) return false

    if (subItem.submenu?.length) {
      return subItem.submenu.some(
        (nested) =>
          location.pathname === nested.path ||
          location.pathname.startsWith(nested.path)
      )
    }

    const hasMoreSpecificMatch = items.some(
      (otherItem) =>
        otherItem.id !== subItem.id &&
        location.pathname.startsWith(otherItem.path) &&
        otherItem.path.length > subItem.path.length
    )
    return !hasMoreSpecificMatch
  }

  const renderIcon = (subItem: MenuItem, active: boolean, isCancelarEnvios: boolean) => {
    const customIcon = CUSTOM_ICONS[subItem.id]
    if (customIcon) {
      return (
        <img
          src={customIcon.src}
          alt={customIcon.alt}
          className="h-4 w-4 object-contain transition-all duration-200 group-hover:scale-110"
        />
      )
    }
    const IconComponent = subItem.icon
    return (
      <IconComponent
        className={`h-4 w-4 transition-all duration-200 ${
          active
            ? isCancelarEnvios
              ? 'text-red-600'
              : 'text-custom-blue-2'
            : 'text-gray-600 group-hover:text-custom-blue group-hover:scale-110'
        }`}
      />
    )
  }

  return (
    <ul
      className={`${
        isCollapsed
          ? 'absolute left-full w-48 bg-white shadow-lg rounded-lg z-[60] animate-slide-right'
          : 'mt-1 ml-6 space-y-1 animate-slide-down py-1'
      }`}
      style={
        isCollapsed
          ? {
              top: '6px',
              left: '0px',
              paddingTop: '0px',
              paddingBottom: '4px',
            }
          : undefined
      }
    >
      {items.map((subItem) => {
        const hasNestedSubmenu = Boolean(subItem.submenu && subItem.submenu.length > 0)
        const active = isItemActive(subItem)
        const isCancelarEnvios = subItem.path === '/envios/cancelar'
        const nestedOpen = isCollapsed || Boolean(isMenuOpen?.(subItem.id))

        if (hasNestedSubmenu) {
          return (
            <li key={subItem.id}>
              <div
                onClick={() => !isCollapsed && onToggle?.(subItem.id)}
                className={`group flex items-center justify-between p-2 rounded-lg cursor-pointer transition-all duration-200 ${
                  active
                    ? 'bg-gray-100 text-gray-700 shadow-sm'
                    : 'text-custom-blue-2 hover:bg-custom-blue-50 hover:text-custom-blue hover:shadow-md hover:scale-[1.02]'
                } active:scale-[0.98]`}
                role="button"
                tabIndex={0}
                aria-expanded={nestedOpen}
                aria-label={subItem.label}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    if (!isCollapsed) onToggle?.(subItem.id)
                  }
                }}
              >
                <div className="flex items-center">
                  {renderIcon(subItem, active, false)}
                  <span className="ml-2 text-sm">{subItem.label}</span>
                </div>
                {!isCollapsed && (
                  <ChevronDownIcon
                    className={`h-3.5 w-3.5 transform transition-all duration-200 ${
                      nestedOpen ? 'rotate-180' : ''
                    } ${active ? 'text-custom-blue' : 'text-gray-400'}`}
                  />
                )}
              </div>

              {nestedOpen && subItem.submenu && (
                <ul className={`${isCollapsed ? 'pl-2' : 'mt-1 ml-4 space-y-1'} py-1`}>
                  {subItem.submenu.map((nestedItem) => {
                    const nestedActive =
                      location.pathname === nestedItem.path ||
                      location.pathname.startsWith(nestedItem.path)
                    const NestedIcon = nestedItem.icon

                    return (
                      <li key={nestedItem.id}>
                        <Link
                          to={nestedItem.path}
                          className={`group flex items-center p-2 rounded-lg transition-all duration-200 ${
                            nestedActive
                              ? 'bg-gray-100 text-gray-700 shadow-sm'
                              : 'text-custom-blue-2 hover:bg-custom-blue-50 hover:text-custom-blue hover:shadow-md hover:scale-[1.02]'
                          } active:scale-[0.98]`}
                        >
                          <NestedIcon
                            className={`h-4 w-4 transition-all duration-200 ${
                              nestedActive
                                ? 'text-custom-blue-2'
                                : 'text-gray-600 group-hover:text-custom-blue group-hover:scale-110'
                            }`}
                          />
                          <span className="ml-2 text-sm">{nestedItem.label}</span>
                        </Link>
                      </li>
                    )
                  })}
                </ul>
              )}
            </li>
          )
        }

        return (
          <li key={subItem.id}>
            <Link
              to={subItem.path}
              className={`group flex items-center p-2 rounded-lg transition-all duration-200 ${
                active
                  ? isCancelarEnvios
                    ? 'bg-red-50 text-red-600 shadow-sm'
                    : 'bg-gray-100 text-gray-700 shadow-sm'
                  : 'text-custom-blue-2 hover:bg-custom-blue-50 hover:text-custom-blue hover:shadow-md hover:scale-[1.02]'
              } active:scale-[0.98]`}
            >
              {renderIcon(subItem, active, isCancelarEnvios)}
              <span className="ml-2 text-sm">{subItem.label}</span>
              {subItem.badge && (
                <span className="ml-auto px-2 py-0.5 bg-red-500 text-white rounded-full text-xs">
                  {subItem.badge}
                </span>
              )}
            </Link>
          </li>
        )
      })}
    </ul>
  )
}

export default React.memo(SidebarSubmenu)
